import React, { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { BaseDialogProps, DialogComponent, DialogContext, DialogInstance, DialogProviderProps } from '@/components/Dialog/DialogContext'

export const GlobalDialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialogs, setDialogs] = useState<DialogInstance[]>([])

  const openDialog = useCallback(<P extends object>(
    Component: DialogComponent<P>,
    props: Omit<P & BaseDialogProps, 'open'>,
  ) => {
    const dialogId = uuidv4()
    
    setDialogs(prevDialogs => [
      ...prevDialogs,
      {
        id: dialogId,
        Component,
        props: {
          ...props,
          open: true,
          onClose: () => {
            props.onClose?.()
            closeDialog(dialogId)
          },
        },
      },
    ])

    return dialogId
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeDialog = useCallback((id: string) => {
    setDialogs(prevDialogs => {
      const dialogIndex = prevDialogs.findIndex(dialog => dialog.id === id)

      if (dialogIndex === -1) return prevDialogs

      // Set dialog to animate out
      const updatedDialogs = [...prevDialogs]

      updatedDialogs[dialogIndex] = {
        ...updatedDialogs[dialogIndex],
        props: {
          ...updatedDialogs[dialogIndex].props,
          open: false,
        },
      }

      // Remove dialog after animation
      setTimeout(() => {
        setDialogs(prev => prev.filter(dialog => dialog.id !== id))
      }, 300) // Adjust timing to match your transition duration

      return updatedDialogs
    })
  }, [])

  const closeAllDialogs = useCallback(() => {
    setDialogs(prevDialogs => 
      prevDialogs.map(dialog => ({
        ...dialog,
        props: {
          ...dialog.props,
          open: false,
        },
      })),
    )

    // Remove all dialogs after animation
    setTimeout(() => {
      setDialogs([])
    }, 300)
  }, [])

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog, closeAllDialogs }}>
      {children}
      {dialogs.map(({ id, Component, props }) => (
        <Component key={id} {...props} />
      ))}
    </DialogContext.Provider>
  )
}